import { v4 as uuidv4 } from "uuid";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import {
  S3Client,
  PutObjectCommand,
  DeleteObjectCommand,
} from "@aws-sdk/client-s3";

const {
  VITE_POOl_ID,
  VITE_AWS_S3_BUCKET_NAME,
  VITE_AWS_S3_REGION,
} = import.meta.env;

const region = VITE_AWS_S3_REGION as string;
const IdentityPoolId = VITE_POOl_ID as string;
const s3BucketName = VITE_AWS_S3_BUCKET_NAME as string;

const cognitoClient = new CognitoIdentityClient({ region });

const s3Client = new S3Client({
  region,
  credentials: fromCognitoIdentityPool({
    client: cognitoClient,
    identityPoolId: IdentityPoolId,
  }),
});

export const UploadCognitoImage = async (file: File) => {
  try {
    const isAllowedImageType =
      file?.type === "image/png" ||
      file?.type === "image/jpeg" ||
      file?.type === "image/jpg" ||
      file?.type === "image/gif";

    if (!isAllowedImageType) {
      throw new Error(
        "Unsupported file type. Only PNG, JPG/JPEG, and GIF are allowed."
      );
    }

    // Generate a unique file name using UUID
    const fileName = `${uuidv4()}-${file?.name}`;

    const uploadParams = {
      Bucket: s3BucketName,
      Key: `images/${fileName}`,
      ContentType: file?.type,
      Body: file,
    };

    // Create and send the upload command to S3
    const command = new PutObjectCommand(uploadParams);
    const data = await s3Client.send(command);


    return {
      success: true,
      data: fileName,
      location: `${fileName}`,
    };
  } catch (error: any) {
    return { success: false, message: error?.message };
  }
};

export const deleteCognitoImage = async (fileName: string) => {
  try {
    const deleteParams = {
      Bucket: s3BucketName,
      Key: `images/${fileName}`,
    };

    const command = new DeleteObjectCommand(deleteParams);

    const data = await s3Client.send(command);

    return {
      success: true,
      message: `File ${fileName} deleted successfully.`,
    };
  } catch (error: any) {
    return { success: false, message: error?.message };
  }
};


const { VITE_VIEW_S3_URL } = import.meta.env;


export const UploadCognitoFiles = async (files: File[]) => {
  try {
    if (!files.length) {
      throw new Error("No files provided.");
    }

    const attachments: string[] = [];

    for (const file of files) {
      // ✅ Generate a unique file name using UUID
      const fileName = `${uuidv4()}-${file.name}`;

      const uploadParams = {
        Bucket: s3BucketName,
        Key: `images/${fileName}`, // ✅ Still uploading inside 'images/' folder
        ContentType: file.type,
        Body: file,
      };

      // Upload to S3
      const command = new PutObjectCommand(uploadParams);
      await s3Client.send(command);

      // ✅ Only append the file name to VITE_VIEW_S3_URL (without 'images/' again)
      const fileUrl = `${VITE_VIEW_S3_URL}${fileName}`;
      attachments.push(fileUrl);
    }

    return {
      success: true,
      attachments, // Array of uploaded file URLs
    };
  } catch (error: any) {
    return { success: false, message: error.message };
  }
};


export const DeleteCognitoFile = async (fileUrl: string) => {
  try {
    // Extract the file key (path) from the URL
    const fileKey = fileUrl.replace(VITE_VIEW_S3_URL, ""); // Remove base URL to get the file path

    const deleteParams = {
      Bucket: s3BucketName,
      Key: `images/${fileKey}`, // Ensure correct path
    };

    // Send delete command to S3
    const command = new DeleteObjectCommand(deleteParams);
    await s3Client.send(command);

    return { success: true };
  } catch (error: any) {
    return { success: false, message: error.message };
  }
};
